import './AiDisclosureBadge.css'

export type AiDisclosureVariant = 'overlay' | 'caption'
export type AiDisclosureAlign = 'start' | 'center'

export interface AiDisclosureBadgeProps {
  /** Le signalement exact, déjà rédigé (« Image générée par IA »). */
  label: string
  /**
   * `overlay` se pose sur le visuel, `caption` se range dessous, dans le flux.
   * L'overlay reste le choix par défaut : il ne peut pas être séparé de
   * l'image par une mise en page qui les éloignerait.
   */
  variant?: AiDisclosureVariant
  /**
   * Position horizontale de la variante `overlay`. `center` existe pour les
   * zones aux angles très arrondis — la zone image d'une `Band` a deux coins à
   * 50 % et masque son débordement, un badge posé dans un angle y serait rogné.
   */
  align?: AiDisclosureAlign
  /**
   * Retire le badge de l'arbre d'accessibilité. À n'utiliser que lorsque la
   * mention figure déjà ailleurs pour les lecteurs d'écran — c'est ce que fait
   * `MediaImage`, dont l'`alt` la porte toujours. Par défaut le badge est
   * annoncé : mieux vaut l'entendre deux fois que pas du tout.
   */
  hiddenFromScreenReaders?: boolean
}

/**
 * Le signalement d'un visuel produit ou modifié par IA — article 50 du
 * règlement (UE) 2024/1689, applicable depuis le 2 août 2026.
 *
 * Il est visible dès l'affichage, sans survol ni interaction, et son texte est
 * du texte réel : jamais incrusté dans l'image, donc toujours lisible, toujours
 * traduisible, toujours sélectionnable. Le fond sombre translucide et le liseré
 * blanc lui gardent son contraste sur une photo claire comme sur une photo
 * sombre — on ne choisit pas la luminosité des images qu'il accompagne.
 */
export function AiDisclosureBadge({
  label,
  variant = 'overlay',
  align = 'start',
  hiddenFromScreenReaders = false,
}: AiDisclosureBadgeProps) {
  const classes = [
    'azoya-ai-disclosure',
    `azoya-ai-disclosure--${variant}`,
    variant === 'overlay' && align === 'center' && 'azoya-ai-disclosure--center',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span className={classes} aria-hidden={hiddenFromScreenReaders || undefined}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2z" />
      </svg>
      {label}
    </span>
  )
}
